import { TASK_STATUS_LABELS } from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { vorhabenQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { Button, SourceBadge } from './ui/primitives'
import { useAufgabenDieserWelt } from '../lib/welt'
import { hintergrundSchliesst } from './ui/hintergrund'
import { nebenBestandVerwerfen } from '../lib/mutationen'

/**
 * **Bestehende Aufgaben einem Vorhaben zuordnen** (21.08.2026,
 * [E-205](../../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * Nils: *„+ Aufgabe zuweisen (aus bestehenden)"*. Der Weg gab es serverseitig schon
 * (`vorhaben.aufgabenZuordnen`, mehrere auf einmal) – bedienbar war er nur über „Alle
 * Aufgaben" und den Filter „ohne Vorhaben", also von der falschen Seite aus: dort sucht man
 * *Aufgaben ohne Klammer*, hier sucht man *die Klammer für eine bekannte Aufgabe*.
 *
 * ## Gesucht wird im Client
 *
 * Der Aufgabenbestand liegt beim Start ohnehin vollständig im Browser (Leitplanke 2), also
 * ist die Suche eine lokale Operation ohne Netz und ohne Wartezeit. Gesucht wird über Titel
 * **und** Kennungen: wer eine Aufgabe zuordnen will, hat oft die Ticketnummer im Kopf und
 * nicht den Wortlaut ihres Titels.
 *
 * ## Erledigtes steht nicht zur Wahl
 *
 * Eine abgeschlossene Aufgabe nachträglich einem Vorhaben zuzuschlagen ändert dessen
 * Fortschrittsbalken rückwirkend – *„18 von 25"* wird zu *„19 von 26"*, ohne dass jemand
 * gearbeitet hat. Wer es trotzdem braucht (eine Altlast nachtragen), macht es in „Alle
 * Aufgaben"; dort ist es eine bewusste Massenaktion und keine Nebenwirkung einer Suche.
 */
export function AufgabeZuordnen({
  vorhabenId,
  vorhabenLabel,
  schliessen,
}: {
  vorhabenId: string
  vorhabenLabel: string
  schliessen: () => void
}) {
  const queryClient = useQueryClient()
  /**
   * **Zuordnen geht nur innerhalb einer Arbeitswelt** (Z-07). Eine Aufgabe an ein Vorhaben
   * einer anderen Welt zu hängen wäre der eine Weg, die Trennung von innen aufzuweichen: das
   * Vorhaben zöge sie in seine Auswertungen, sichtbar wäre sie dort trotzdem nicht.
   */
  const { aufgaben } = useAufgabenDieserWelt()
  const { data: vorhaben = [] } = useQuery(vorhabenQuery)

  const [suche, setSuche] = useState('')
  const [gewaehlt, setGewaehlt] = useState<string[]>([])
  const [fehler, setFehler] = useState<string | null>(null)

  const treffer = useMemo(() => {
    const gesucht = suche.trim().toLowerCase()
    return (
      aufgaben
        .filter((a) => a.status !== 'erledigt' && a.status !== 'zurueckgestellt')
        /** Was schon hier liegt, braucht nicht zugeordnet zu werden. */
        .filter((a) => a.vorhabenId !== vorhabenId)
        .filter(
          (a) =>
            !gesucht ||
            a.title.toLowerCase().includes(gesucht) ||
            (a.alleKennungen ?? '').toLowerCase().includes(gesucht),
        )
        /**
         * **Höchstens fünfzig.** Ohne Suchbegriff wären es 150 Zeilen, und eine Liste, in der
         * man scrollt, statt zu suchen, lädt zum Raten ein. Wie viele es insgesamt sind, steht
         * darunter – eine stille Kürzung wäre die schlechtere Hälfte dieser Entscheidung.
         */
        .slice(0, 50)
    )
  }, [aufgaben, suche, vorhabenId])

  const gesamtTreffer = useMemo(() => {
    const gesucht = suche.trim().toLowerCase()
    return aufgaben.filter(
      (a) =>
        a.status !== 'erledigt' &&
        a.status !== 'zurueckgestellt' &&
        a.vorhabenId !== vorhabenId &&
        (!gesucht ||
          a.title.toLowerCase().includes(gesucht) ||
          (a.alleKennungen ?? '').toLowerCase().includes(gesucht)),
    ).length
  }, [aufgaben, suche, vorhabenId])

  const zuordnen = useMutation({
    mutationFn: () => trpc.vorhaben.aufgabenZuordnen.mutate({ taskIds: gewaehlt, vorhabenId }),
    onSuccess: async () => {
      await nebenBestandVerwerfen(queryClient)
      await queryClient.invalidateQueries({ queryKey: ['vorhaben'] })
      schliessen()
    },
    onError: (e: Error) => setFehler(e.message),
  })

  function umschalten(id: string) {
    setGewaehlt((v) => (v.includes(id) ? v.filter((x) => x !== id) : [...v, id]))
  }

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-50 flex items-start justify-center bg-[rgb(23_22_20/0.38)] p-3 pt-[4vh] sm:p-6 sm:pt-[6vh]"
      {...hintergrundSchliesst(schliessen)}
      role="dialog"
      aria-modal="true"
      aria-label="Aufgabe zuweisen"
    >
      <div className="flex max-h-[min(90vh,100%)] sm:max-h-[min(80vh,100%)] w-full min-w-0 max-w-[640px] flex-col rounded-card border border-line bg-surface p-[18px] shadow-card">
        <h3 className="text-sm font-bold">Aufgabe zuweisen an „{vorhabenLabel}"</h3>
        <p className="mt-1 text-[11.5px] text-muted">
          Aus dem bestehenden Bestand. Eine Aufgabe gehört zu höchstens einem Vorhaben – was schon
          eines hat, wechselt hierher.
        </p>

        <input
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          autoFocus
          placeholder="Titel oder Ticketnummer …"
          aria-label="Aufgabe suchen"
          className="mt-3 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px]"
        />

        <div className="mt-2 min-h-0 flex-1 overflow-y-auto">
          {treffer.length === 0 && (
            <p className="py-3 text-[12.5px] text-muted">
              Nichts gefunden. Gesucht wird über Titel und Ticketnummern – Erledigtes steht nicht
              zur Wahl.
            </p>
          )}

          {treffer.map((a) => {
            /** Wo sie bisher liegt – das ist die Angabe, die einen Fehlgriff verhindert. */
            const bisher = a.vorhabenId
              ? (vorhaben.find((v) => v.id === a.vorhabenId)?.label ?? 'anderes Vorhaben')
              : null
            return (
              <label
                key={a.id}
                className="flex cursor-pointer items-center gap-2 border-b border-line py-1.5 text-[12.5px] last:border-b-0 hover:bg-[#fafaf8]"
              >
                <input
                  type="checkbox"
                  checked={gewaehlt.includes(a.id)}
                  onChange={() => umschalten(a.id)}
                  className="cursor-pointer"
                />
                <span className="w-[80px] shrink-0 text-[11px] text-muted">
                  {TASK_STATUS_LABELS[a.status] ?? a.status}
                </span>
                <span className="min-w-0 flex-1 truncate" title={a.title}>
                  {a.title}
                </span>
                {bisher && (
                  <span
                    className="shrink-0 text-[10.5px] text-[#8a6100]"
                    title={`Liegt bisher in „${bisher}" und wechselt hierher`}
                  >
                    aus „{bisher}"
                  </span>
                )}
                {a.externalKey && <SourceBadge source={a.sourceId} label={a.externalKey} />}
              </label>
            )
          })}

          {gesamtTreffer > treffer.length && (
            <p className="py-2 text-[11px] text-muted">
              {gesamtTreffer - treffer.length} weitere passen – such genauer.
            </p>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
          <Button
            variant="primary"
            disabled={gewaehlt.length === 0 || zuordnen.isPending}
            onClick={() => {
              setFehler(null)
              zuordnen.mutate()
            }}
          >
            {gewaehlt.length === 0 ? 'nichts gewählt' : `${gewaehlt.length} zuordnen`}
          </Button>
          <Button onClick={schliessen}>Abbrechen</Button>
          {fehler && <span className="text-[12px] text-pool-sofort">{fehler}</span>}
        </div>
      </div>
    </div>
  )
}
